// shared-lib/fixture-types/productRepresentation.js
//
// What does an object's DATA say about the product it shows, and may a
// builder resize it? These lived in the web app (`resolveRenderSource.js`,
// `normalObjectTypes.js`) and are needed by the cabinet-run engine too: a run
// that rebuilds an appliance's install (a range hood, a fridge surround) must
// not silently replace a product the user chose, nor stretch one whose
// catalog doc never claimed it could be stretched.
//
// Here rather than in a new top-level module because this is the same kind of
// question `fixture-types` already answers — read an object's identity off
// its own fields, pure, no SDK deps, shared by web and functions.

/**
 * Prefix every built-in (app-drawn) model id carries: the bare `procedural`
 * id, the style suffixes (`procedural-recessed` / `-fullwall`), the per-type
 * variant ids (`procedural-range-36in`, `procedural-sink-faucet-widespread`)
 * and `procedural-wall-hung`.
 */
const PROCEDURAL_MODEL_ID_PREFIX = 'procedural';

/**
 * Is this model id a GENERIC — a build the app draws itself — rather than a
 * reference to a `unifiedObjects` catalog document?
 *
 * The one place the string rule lives. Every caller of it is doing the same
 * thing for the same reason: a generic id must not be handed to a catalog
 * fetch, which would spend a document read to learn nothing and then treat
 * the absent doc as a missing product. It was spelled out inline in
 * `useDesignCenter.swapFixture` and nowhere else, so the design-package
 * resolve, the package apply and the starter-ref audit each saw a generic as
 * a broken catalog id.
 *
 * `none` ("Original Geometry") counts: it is likewise not a catalog id, and
 * the swap engine already routes it down the same client path.
 */
const isGenericModelId = (modelId) =>
  typeof modelId === 'string' &&
  (modelId === 'none' || modelId.startsWith(PROCEDURAL_MODEL_ID_PREFIX));

/**
 * A usable model reference. `modelId === 'none'` is the canonical
 * "Original Geometry" sentinel and is NOT a GLB.
 */
const objectHasGlbModel = (object) =>
  !!(object?.modelUrl || (object?.modelId && object.modelId !== 'none'));

/** An explicit "draw the placeholder", even with a stale `modelUrl` present. */
const objectIsExplicitOptOut = (object) =>
  object?.isProcedural === true ||
  object?.selectedModelType === 'procedural' ||
  object?.selectedModelType === 'none' ||
  object?.modelId === 'none';

/** Is this object DRAWN as a catalog product rather than the placeholder? */
const objectShowsGlb = (object) =>
  objectHasGlbModel(object) && !objectIsExplicitOptOut(object);

/**
 * Does the design still stand in for this object with a PLACEHOLDER — no
 * product picked for it — rather than show a product someone chose?
 *
 * `objectShowsGlb` alone is not the answer: a generic id
 * (`procedural-range-36in`) with no opt-out marker reads as "has a model",
 * yet it names the app's own build, not a catalog product. The take-off bids
 * such an object as an allowance and the books' allowance reconcile waits for
 * it to be picked — both ask this, so neither spells the rule out itself.
 */
const objectIsPlaceholder = (object) =>
  isGenericModelId(object?.modelId) || !objectShowsGlb(object);

/**
 * Fields that exist ONLY to say "draw the placeholder".
 *
 * `selectedModelType` and `modelId` are not here: they are ordinary fields
 * that only mark an opt-out at particular VALUES, which `clearProceduralOptOut`
 * handles separately.
 */
const PROCEDURAL_ONLY_FIELDS = ['isProcedural', 'proceduralStyle'];

/**
 * Return a copy of `object` with every placeholder marker removed — the
 * inverse of `objectIsExplicitOptOut` above, and deliberately its neighbour.
 *
 * The reader and the clearer were in different packages, and they disagreed:
 * the client model picker cleared two of the four markers, the cloud swap
 * tools cleared none, and the renderer read all four. A fixture switched to
 * the placeholder and then swapped to a real product therefore kept rendering
 * as the placeholder — at the new product's dimensions, which reads as a
 * sizing bug rather than a rendering one. Measured on dev 2026-09-04: 6
 * objects stuck that way (2 sinks, 2 sink faucets, 2 mirrors).
 *
 * `selectedModelType` is dropped only when it HOLDS an opt-out value — 'glb'
 * is legitimate on an object that carries a model. `modelId` is never touched:
 * the caller is in the middle of assigning the new one.
 */
function clearProceduralOptOut(object) {
  const out = { ...object };
  for (const key of PROCEDURAL_ONLY_FIELDS) delete out[key];
  if (
    out.selectedModelType === 'procedural' ||
    out.selectedModelType === 'none'
  ) {
    delete out.selectedModelType;
  }
  return out;
}

/**
 * Does the CATALOG affirmatively say this product may be stretched?
 *
 * `isScalable === true` is an opt-in claim about a specific model — it is
 * authored to be scaled to arbitrary dimensions, and `SceneObject` reads the
 * same flag before applying the object's dimensions to the GLB. **Absence is
 * not permission**: unset or `false` means the catalog has made no such
 * claim, and a product is whatever size that product is.
 */
const productIsStretchable = (object) => object?.isScalable === true;

/**
 * Fields that belong to an APPLIED GLB and to nothing else.
 *
 * Promoted from `web-app/src/hooks/objectModelUpdate/clearGlbModelFields.js`
 * (2026-09-28) because a shared-lib builder needs it too: the vanity run
 * redraws a basin whose product cannot fit its deck, and a run engine cannot
 * import a web-app hook. The web module now re-exports this one, so the
 * reader (`objectShowsGlb`, above) and the clearer live in the same file —
 * which is the lesson `clearProceduralOptOut` was written for, one direction
 * over.
 */
const GLB_ONLY_FIELDS = [
  'modelId',
  'modelUrl',
  'modelName',
  'modelDimensions',
  'scaleFactor',
  'isScalable',
  'centerOffset',
  'sink-cutout',
  'faucet-mounting-points',
];

/**
 * A copy of `object` with every GLB-only field removed — for when the built-in
 * render takes over, so nothing downstream keeps acting on a model that is no
 * longer applied.
 *
 * `sink-cutout` is the one that bites hardest and is the reason this is
 * shared: it is the product's RIM POLYGON, and `resolveSinkCutoutSpec` cuts
 * the countertop to it in preference to anything else. Leave it behind and the
 * deck keeps the old product's opening — a rectangular Kohler rim around the
 * ellipsoid the placeholder renderer draws, which is exactly what a user sees
 * and reports. Cleared, that function's own fallback cuts the ellipse the
 * renderer draws, "so the hole is what the renderer draws".
 *
 * Pure.
 */
function clearGlbModelFields(object) {
  const out = { ...object };
  for (const key of GLB_ONLY_FIELDS) delete out[key];
  return out;
}

/**
 * May a BUILDER give this object a new size? True for the procedural
 * placeholder (the builder draws it, so the size is the builder's) and for a
 * product whose doc opts in. False for any other chosen product — its size is
 * the product's, and writing over it stretches the mesh (the read-side twin
 * of `isDimensionEditable`, which withholds the size editor for the same
 * reason).
 */
const mayResizeObject = (object) =>
  !objectShowsGlb(object) || productIsStretchable(object);

module.exports = {
  GLB_ONLY_FIELDS,
  clearGlbModelFields,
  PROCEDURAL_MODEL_ID_PREFIX,
  PROCEDURAL_ONLY_FIELDS,
  isGenericModelId,
  clearProceduralOptOut,
  objectHasGlbModel,
  objectIsExplicitOptOut,
  objectShowsGlb,
  objectIsPlaceholder,
  productIsStretchable,
  mayResizeObject,
};
